import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useParams } from "react-router-dom";
import useFooddyStore from "../../store";
import Rating from "../Rating/Rating";
import { Card, Empty, Icon, OutlineButton, Page, SectionTitle, Segmented, Skeleton, rupees } from "./parts";
import useOverviewData from "./useOverviewData";
import MonthChart from "./MonthChart";
import Leaderboard from "./Leaderboard";
import Friends from "./Friends";
import ShareView from "./ShareView";
import { StoreMix, TopPurchases } from "./Breakdown";

const PLATFORM_NAMES = { amazon: "Amazon.in", flipkart: "Flipkart" };

const TopBar = styled.header`
  position: sticky;
  top: 0;
  z-index: 4;
  height: 60px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 32px;
  border-bottom: 1px solid var(--line);
  background: color-mix(in oklab, var(--surface) 90%, transparent);
  backdrop-filter: blur(10px);

  @media (max-width: 700px) {
    padding: 0 16px;
  }
`;

const Layout = styled.div`
  max-width: 1120px;
  margin: 0 auto;
  padding: 28px 32px 120px;
  display: grid;
  grid-template-columns: 200px minmax(0, 1fr);
  gap: 40px;
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
    padding: 20px 16px 80px;
  }
`;

const Nav = styled.nav`
  position: sticky;
  top: 88px;
  display: flex;
  flex-direction: column;
  gap: 2px;

  @media (max-width: 900px) {
    display: none;
  }
`;

const NavButton = styled.button`
  height: 36px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 12px;
  border: 0;
  border-radius: 9px;
  text-align: left;
  font-size: 13px;
  font-weight: ${(props) => (props.$active ? 600 : 500)};
  background: ${(props) => (props.$active ? "var(--acc-soft)" : "transparent")};
  color: ${(props) => (props.$active ? "var(--acc)" : "var(--ink2)")};
  transition: background 0.2s, color 0.2s;

  &:hover {
    color: var(--ink);
    background: ${(props) => (props.$active ? "var(--acc-soft)" : "var(--surface2)")};
  }
`;

const NavHeading = ({ children, first }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: first ? "0 12px 6px" : "18px 12px 6px",
      fontSize: 10.5,
      fontWeight: 600,
      letterSpacing: ".08em",
      color: "var(--ink3)",
    }}
  >
    {children}
    <span style={{ flex: 1, height: 1, background: "var(--line)" }} />
  </div>
);

const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: 14px;
  overflow: hidden;
`;

// Divider lines on each tile's right and bottom edge; the ones on the outer
// edge are clipped, so a half-empty last row has no grey gap.
const Kpi = ({ label, value, sub, subColor, valueColor }) => (
  <div style={{ padding: "14px 16px", boxShadow: "1px 0 0 var(--line), 0 1px 0 var(--line)" }}>
    <div style={{ fontSize: 11.5, color: "var(--ink3)" }}>{label}</div>
    <div style={{ fontSize: 22, fontWeight: 600, letterSpacing: "-.01em", marginTop: 4, color: valueColor }}>{value}</div>
    <div style={{ fontSize: 11, color: subColor ?? "var(--ink3)", marginTop: 3 }}>{sub}</div>
  </div>
);

const TwoUp = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 14px;
`;

const ordinal = (n) => {
  const tens = n % 100;
  if (tens >= 11 && tens <= 13) return `${n}th`;
  return `${n}${["th", "st", "nd", "rd"][n % 10] || "th"}`;
};

const timeAgo = (date) => {
  if (!date) return null;
  const minutes = Math.round((Date.now() - new Date(date).getTime()) / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  return new Date(date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};

// The spending dashboard the extension opens after "Calculate"
// (/:userid/:platform/:spenderId), in the extension's design.
function Overview() {
  const params = useParams();
  const { swiggyActive, setSwiggyActive, showShare, setShowShare, userName, userPrflPic } = useFooddyStore();
  const platform = swiggyActive ? "amazon" : "flipkart";
  const platformName = PLATFORM_NAMES[platform];
  const [year, setYear] = useState(null);
  const [showRating, setShowRating] = useState(false);
  const [active, setActive] = useState("overview");
  const data = useOverviewData(params.userid, platform, year);

  const summary = data.summaries?.[platform];
  const years = useMemo(
    () => [...(summary?.yearlySpending ?? [])].sort((a, b) => b.year - a.year),
    [summary]
  );

  // Start on the newest year with orders whenever the platform's data loads.
  useEffect(() => {
    if (data.loading) return;
    setYear(years[0]?.year ?? new Date().getFullYear());
  }, [data.loading, platform]);

  const row = years.find((y) => y.year === year);
  const lastRow = years.find((y) => y.year === year - 1);
  // Year-on-year change. The current year is compared with the same months of
  // last year (Jan .. this month), not last year's full total.
  const now = new Date();
  const partialYear = year === now.getFullYear();
  const lastYearTotal = partialYear
    ? data.months.loading
      ? 0
      : data.months.previous.slice(0, now.getMonth() + 1).reduce((a, b) => a + b, 0)
    : lastRow?.totalAmount;
  const change = row && lastYearTotal ? ((row.totalAmount - lastYearTotal) / lastYearTotal) * 100 : null;
  const changeLabel = partialYear
    ? `vs Jan–${now.toLocaleString("en-US", { month: "short" })} ${year - 1}`
    : `vs ${year - 1}`;
  const rank = Number(data.profile?.order_amount_rank);

  const scrollTo = (id) => {
    setShowShare(false);
    setActive(id);
    if (id === "overview") window.scrollTo({ top: 0, behavior: "smooth" });
    else document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const openShare = () => {
    setActive("share");
    setShowShare(true);
    window.scrollTo({ top: 0 });
  };

  const nav = [
    { id: "overview", label: "Overview", icon: "query_stats", on: () => scrollTo("overview") },
    { id: "leaderboard", label: "Leaderboard", icon: "leaderboard", on: () => scrollTo("leaderboard") },
    { id: "friends", label: "Challenge friends", icon: "group", on: () => scrollTo("friends") },
    { id: "share", label: "Share my spend", icon: "ios_share", on: openShare },
  ];

  return (
    <Page>
      <TopBar>
        <img src="/icon.png" alt="" style={{ width: 28, height: 28, objectFit: "contain", flex: "none" }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>Spending Calculator for Amazon™ and Flipkart™</div>
          <div style={{ fontSize: 11.5, color: "var(--ink3)" }}>Spend · {platformName}</div>
        </div>
        <Segmented
          value={platform}
          onChange={(value) => setSwiggyActive(value === "amazon")}
          options={[
            { value: "amazon", label: "Amazon.in" },
            { value: "flipkart", label: "Flipkart" },
          ]}
        />
        {userName && (
          <span style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, fontWeight: 500 }}>
            <img src={userPrflPic} alt="" style={{ width: 28, height: 28, borderRadius: "50%", background: "var(--surface2)" }} />
            <span className="hide-sm">{userName}</span>
          </span>
        )}
      </TopBar>

      <Layout>
        <Nav>
          <NavHeading first>SPEND</NavHeading>
          {nav.map((item) => (
            <NavButton key={item.id} $active={active === item.id} onClick={item.on}>
              <Icon name={item.icon} size={18} />
              {item.label}
            </NavButton>
          ))}
          <NavHeading>MORE</NavHeading>
          <NavButton onClick={() => setShowRating(true)}>
            <Icon name="star" size={18} />
            Rate us
          </NavButton>
          <div
            style={{
              marginTop: 16,
              padding: 12,
              borderRadius: 10,
              border: "1px dashed var(--line)",
              fontSize: 11.5,
              color: "var(--ink3)",
              lineHeight: 1.5,
            }}
          >
            Totals come from the orders you calculated with the extension. Calculate again to update them.
          </div>
        </Nav>

        <main style={{ display: "flex", flexDirection: "column", gap: 20, minWidth: 0 }}>
          {showShare ? (
            <ShareView platform={platform} platformName={platformName} onBack={() => scrollTo("overview")} />
          ) : (
          <>
          <SectionTitle
            title="Spend overview"
            sub={
              data.loading
                ? "Loading your orders…"
                : data.noUser
                ? `No ${platformName} orders calculated yet`
                : [
                    `${platformName}`,
                    row ? `${row.orderCount} orders in ${year}` : null,
                    summary?.lastSyncedAt ? `updated ${timeAgo(summary.lastSyncedAt)}` : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")
            }
          >
            {years.length > 1 && (
              <Segmented
                value={year}
                onChange={setYear}
                options={years.slice(0, 4).map((y) => ({ value: y.year, label: String(y.year) }))}
              />
            )}
            <OutlineButton onClick={openShare}>
              <Icon name="ios_share" size={16} />
              Share
            </OutlineButton>
          </SectionTitle>

          {data.loading ? (
            <>
              <Skeleton $h={92} />
              <Skeleton $h={240} />
            </>
          ) : data.noUser ? (
            <Card>
              <Empty icon="receipt_long" title={`No ${platformName} spend yet`}>
                Open {platformName}, sign in, click the extension icon and choose “Calculate {platform === "amazon" ? "Amazon" : "Flipkart"} spend”.
                Your totals show up here.
              </Empty>
            </Card>
          ) : (
            <>
              <KpiGrid>
                <Kpi
                  label={`Total spent · ${year}`}
                  value={rupees(row?.totalAmount)}
                  sub={
                    change === null
                      ? `${platformName}`
                      : `${change >= 0 ? "▲" : "▼"} ${Math.abs(change).toFixed(1)}% ${changeLabel}`
                  }
                  subColor={change === null ? undefined : change >= 0 ? "var(--bad)" : "var(--good)"}
                />
                <Kpi label="Orders" value={row?.orderCount ?? 0} sub={`in ${year}`} />
                <Kpi
                  label="Average order"
                  value={row?.orderCount ? rupees(row.totalAmount / row.orderCount) : "—"}
                  sub="per order"
                />
                <Kpi
                  label="All-time spent"
                  value={rupees(summary?.totalAmountSpent ?? data.total)}
                  sub={years.length ? `since ${years[years.length - 1].year}` : "all orders"}
                  valueColor="var(--acc)"
                />
                <Kpi
                  label="Your rank"
                  value={rank ? `#${rank}` : "—"}
                  sub={
                    rank && data.userCount
                      ? `${ordinal(rank)} of ${data.userCount.toLocaleString("en-IN")} users`
                      : "among all spenders"
                  }
                />
              </KpiGrid>

              <MonthChart year={year} months={data.months} />

              <TwoUp>
                <StoreMix
                  loading={data.loading}
                  summaries={data.summaries}
                  onSwitch={(key) => setSwiggyActive(key === "amazon")}
                />
                <TopPurchases loading={data.loading} products={data.topProducts} platformName={platformName} />
              </TwoUp>
            </>
          )}

          <Leaderboard
            loading={data.loading}
            leaders={data.leaders}
            leaderId={data.leaderId}
            userName={userName}
            avatar={userPrflPic}
            platformName={platformName}
          />

          <Friends userId={params.userid} platformName={platformName} total={data.total} userName={userName} />
          </>
          )}
        </main>
      </Layout>

      {showRating && (
        <div style={{ position: "fixed", inset: 0, zIndex: 50 }}>
          <Rating ratingCloseFn={setShowRating} />
        </div>
      )}
    </Page>
  );
}

export default Overview;
